import React, { useState, useEffect } from 'react';
import { Gift, Loader2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

export default function ReferralProgram() {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    referrer_reward_amount: 0,
    referee_reward_amount: 0,
    is_active: false,
    referrer_sms_message: ''
  });

  // Default SMS message template
  const defaultSmsMessage = 'Thank you for referring {customer_name}! We\'ve added ${reward_amount} to your wallet for future bookings.';

  // Load referral program settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedBusiness?.id || !supabase) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('referral_program')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error loading referral program settings:', error);
          return;
        }

        if (data) {
          setSettings({
            referrer_reward_amount: parseFloat(data.referrer_reward_amount) || 0,
            referee_reward_amount: parseFloat(data.referee_reward_amount) || 0,
            is_active: data.is_active || false,
            referrer_sms_message: data.referrer_sms_message || ''
          });
        }
      } catch (err) {
        console.error('Error loading referral program settings:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [selectedBusiness?.id]);

  const handleSave = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setSaving(true);

      // Validate amounts are non-negative
      if (settings.referrer_reward_amount < 0 || settings.referee_reward_amount < 0) {
        toast.error('Reward amounts cannot be negative');
        return;
      }

      const { error } = await supabase
        .from('referral_program')
        .upsert({
          business_id: selectedBusiness.id,
          referrer_reward_amount: settings.referrer_reward_amount,
          referee_reward_amount: settings.referee_reward_amount,
          is_active: settings.is_active,
          referrer_sms_message: settings.referrer_sms_message.trim() || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'business_id'
        });

      if (error) {
        console.error('Error saving referral program settings:', error);
        toast.error('Failed to save settings');
        return;
      }

      toast.success('Referral program settings saved successfully');
    } catch (err) {
      console.error('Error saving referral program settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Referral Program Settings</h2>
          <p className="text-gray-600">
            Configure your referral program rewards and activation status
          </p>
        </div>

        <div className="space-y-6">
          {/* Active/Inactive Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <label className="text-sm font-semibold text-gray-900">Program Status</label>
              <p className="text-sm text-gray-600 mt-1">
                {settings.is_active ? 'Referral program is currently active' : 'Referral program is currently inactive'}
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, is_active: !settings.is_active })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.is_active ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Referrer Reward Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Referrer Reward Amount
            </label>
            <p className="text-sm text-gray-600 mb-3">
              The amount given to the person who refers someone (the referrer)
            </p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.referrer_reward_amount}
                onChange={(e) => setSettings({
                  ...settings,
                  referrer_reward_amount: parseFloat(e.target.value) || 0
                })}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Referee Reward Amount */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Referee Reward Amount
            </label>
            <p className="text-sm text-gray-600 mb-3">
              The amount given to the person who was referred (the referee)
            </p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.referee_reward_amount}
                onChange={(e) => setSettings({
                  ...settings,
                  referee_reward_amount: parseFloat(e.target.value) || 0
                })}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Referrer SMS Message */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Referrer SMS Message
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Customize the SMS message sent to referrers when their referral booking is paid. 
              Use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{'{customer_name}'}</code> for the referred customer's name 
              and <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{'{reward_amount}'}</code> for the reward amount.
            </p>
            
            {/* Default Message Display */}
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-semibold text-gray-700 mb-1">Default Message:</p>
              <p className="text-sm text-gray-600 italic">{defaultSmsMessage}</p>
            </div>

            <textarea
              value={settings.referrer_sms_message}
              onChange={(e) => setSettings({
                ...settings,
                referrer_sms_message: e.target.value
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
              placeholder={defaultSmsMessage}
            />
            <p className="text-xs text-gray-500 mt-2">
              Leave empty to use the default message above.
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Settings</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

